import { useCallback, useEffect, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import {
  downloadPgpChartsCsv,
  fetchPgpCredits,
  updateGeniusCreditFalseFlag,
  updatePubMappingStatus,
} from '../api/pgpClient'
import {
  PGP_FALSE_CREDIT_STATUS,
  PGP_STATUS_OPTIONS,
  isPgpChartsExcludedStatus,
} from '../utils/snoop/statusOptions'
import {
  PLACEHOLDER_PGP_CREDIT,
  PLACEHOLDER_PGP_PERSON,
} from '../utils/snoop/placeholderColumns'

function PgpNewWeekLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
      <span className="inline-flex items-center gap-1.5" title="Not on last week's charts">
        <span className="inline-block h-3 w-6 rounded border border-violet-200 bg-violet-50" />
        New this week
      </span>
    </div>
  )
}

const VIEW_OPTIONS = [
  { value: 'person', label: 'By artist' },
  { value: 'credit', label: 'By credit' },
]

/** Chip order for the toolbar; the API still uses the stored chart labels. */
const PGP_CHART_CHIP_ORDER = [
  'Top 40',
  'Rhythmic',
  'Urban',
  'All Latin Published',
  'Country',
  'Hot 100',
]

const PGP_CHART_CHIP_LABELS = {
  'All Latin Published': 'Latin',
}

function chartChipLabel(label) {
  return PGP_CHART_CHIP_LABELS[label] || label
}

function orderChartChips(labels) {
  const available = [...new Set((labels || []).map(String).filter(Boolean))]
  const rank = new Map(PGP_CHART_CHIP_ORDER.map((label, i) => [label, i]))
  return available.sort((a, b) => {
    const ai = rank.has(a) ? rank.get(a) : 1000
    const bi = rank.has(b) ? rank.get(b) : 1000
    if (ai !== bi) return ai - bi
    return a.localeCompare(b)
  })
}

/** `2026-09-05` → `Sep 5, 2026`. Parsed as local time, not UTC, to avoid an off-by-one day. */
function formatChartWeek(value) {
  const s = String(value || '').slice(0, 10)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return s
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Whole weeks between a chart week and today; null when unparseable. */
function weeksOld(value) {
  const s = String(value || '').slice(0, 10)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (Number.isNaN(d.getTime())) return null
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  return days < 0 ? 0 : Math.floor(days / 7)
}

function creditTypeFromToggles(includeWriters, includeProducers) {
  // Both on (or both off, which the toggles prevent) means no filter.
  if (includeWriters && includeProducers) return undefined
  if (includeWriters) return 'writer'
  if (includeProducers) return 'producer'
  return undefined
}

export default function PgpChartsPage() {
  const [grain, setGrain] = useState('person')
  const [includeWriters, setIncludeWriters] = useState(true)
  const [includeProducers, setIncludeProducers] = useState(true)
  const [includeIgnored, setIncludeIgnored] = useState(false)
  const [selectedCharts, setSelectedCharts] = useState([])
  const [availableCharts, setAvailableCharts] = useState([])
  // Chart week (the Mediabase printed date) + ingest week, so a stale week is
  // obvious rather than silently looking like this week's charts.
  const [scope, setScope] = useState({ scopeWeek: null, ingestWeek: null })
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState(null)

  const isPersonGrain = grain === 'person'
  const creditType = creditTypeFromToggles(includeWriters, includeProducers)
  // Empty / all-selected → omit the filter (the API treats omit as every chart).
  const chartsFilter =
    availableCharts.length &&
    selectedCharts.length &&
    selectedCharts.length < availableCharts.length
      ? selectedCharts
      : undefined

  const fetchData = useCallback(async () => {
    const data = await fetchPgpCredits({
      charts: chartsFilter,
      creditType,
      grain,
      includeIgnored,
    })
    const nextAvailable = data?.filters?.available_charts
    if (Array.isArray(nextAvailable)) {
      setAvailableCharts(orderChartChips(nextAvailable.map(String)))
    }
    setScope({
      scopeWeek: data?.filters?.scope_week ?? null,
      ingestWeek: data?.filters?.week ?? null,
    })
    return data
  }, [chartsFilter, creditType, grain, includeIgnored])

  // All charts on by default; drop labels that disappear after a week refresh.
  useEffect(() => {
    if (!availableCharts.length) return
    setSelectedCharts((prev) => {
      const allowed = new Set(availableCharts)
      const kept = prev.filter((c) => allowed.has(c))
      if (!prev.length || !kept.length) return [...availableCharts]
      return kept.length === prev.length ? prev : kept
    })
  }, [availableCharts])

  const editableStatus = useMemo(() => {
    function creditsTw(row) {
      const n = Number(row?.['Credits TW'])
      return Number.isFinite(n) ? n : 0
    }

    function isFalseCreditRow(row) {
      return Boolean(row?.['False Credit'])
    }

    // On person grain only a single-credit row can be flagged — with several
    // credits it is ambiguous which one is wrong.
    function canMarkFalseCredit(row) {
      if (!isPersonGrain) return true
      return creditsTw(row) === 1
    }

    function statusOptionsForRow(row) {
      if (!canMarkFalseCredit(row) && !isFalseCreditRow(row)) {
        return PGP_STATUS_OPTIONS
      }
      return [
        ...PGP_STATUS_OPTIONS,
        { value: PGP_FALSE_CREDIT_STATUS, label: 'False Credit' },
      ]
    }

    async function saveFalseCreditFlag(row, isFalseCredit) {
      const creditId = row?.['Credit History ID']
      const songId = row?.['Genius Song ID']
      const artistId = row?.['Genius Artist ID']
      const role = row?.['Credit Type']
      const hasCreditId = creditId != null && String(creditId).trim() !== ''
      const hasNaturalKey =
        songId != null &&
        String(songId).trim() !== '' &&
        artistId != null &&
        String(artistId).trim() !== '' &&
        Boolean(role)
      if (!hasCreditId && !hasNaturalKey) {
        throw new Error('Missing Credit History ID / song+artist+type for false credit')
      }
      await updateGeniusCreditFalseFlag({
        id: creditId,
        geniusSongId: songId,
        geniusArtistId: artistId,
        creditType: role,
        isFalseCredit: Boolean(isFalseCredit),
      })
    }

    return {
      Status: {
        target: 'pub_mappings',
        uidColumn: 'Genius Artist ID',
        rowIdColumn: isPersonGrain ? 'Pub Mapping ID' : 'Credit History ID',
        options: PGP_STATUS_OPTIONS,
        getOptions: statusOptionsForRow,
        getValue: (row) =>
          isFalseCreditRow(row) ? PGP_FALSE_CREDIT_STATUS : row?.Status ?? '',
        optimisticPatch: (next) =>
          next === PGP_FALSE_CREDIT_STATUS
            ? { 'False Credit': true }
            : { Status: next || null, 'False Credit': false },
        removeAfterSave: true,
        shouldRemoveRow: (status) =>
          !includeIgnored &&
          (status === PGP_FALSE_CREDIT_STATUS || isPgpChartsExcludedStatus(status)),
        onSave: async ({ status, row, mappingId, uid }) => {
          if (status === PGP_FALSE_CREDIT_STATUS) {
            if (!canMarkFalseCredit(row)) {
              throw new Error(
                'False Credit is only available for artists with a single credit this week',
              )
            }
            await saveFalseCreditFlag(row, true)
            return
          }

          // Moving off False Credit clears the flag before writing the status.
          if (isFalseCreditRow(row)) {
            await saveFalseCreditFlag(row, false)
          }

          const pubId = isPersonGrain
            ? mappingId ?? row?.['Pub Mapping ID']
            : row?.['Pub Mapping ID']
          const artistId = uid ?? row?.['Genius Artist ID']
          const hasPubId = pubId != null && String(pubId).trim() !== ''
          const hasArtistId = artistId != null && String(artistId).trim() !== ''
          if (!hasPubId && !hasArtistId) {
            throw new Error('Missing Pub Mapping ID / Genius Artist ID for status update')
          }
          await updatePubMappingStatus({
            id: pubId,
            geniusArtistId: artistId,
            status,
            name: row?.Name,
            geniusUrl: row?.['Genius URL'],
          })
        },
      },
    }
  }, [includeIgnored, isPersonGrain])

  function toggleChart(label) {
    setSelectedCharts((prev) => {
      if (prev.includes(label)) {
        // Keep at least one chart selected.
        if (prev.length <= 1) return prev
        return prev.filter((c) => c !== label)
      }
      return [...prev, label]
    })
  }

  async function handleExport() {
    setExportBusy(true)
    setExportError(null)
    try {
      await downloadPgpChartsCsv({
        charts: chartsFilter,
        creditType,
        grain,
        includeIgnored,
      })
    } catch (err) {
      setExportError(err?.message || 'Export failed')
    } finally {
      setExportBusy(false)
    }
  }

  function toggleWriters() {
    setIncludeWriters((on) => (on && !includeProducers ? true : !on))
  }

  function toggleProducers() {
    setIncludeProducers((on) => (on && !includeWriters ? true : !on))
  }

  const searchActions = (
    // Wrap rather than scroll: the chart chips were being cut off at the right
    // edge with no affordance that more existed.
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 pb-0.5">
      <div className="inline-flex shrink-0 rounded-lg border border-gray-300 bg-white p-0.5 shadow-sm">
        {VIEW_OPTIONS.map((opt) => {
          const on = grain === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setGrain(opt.value)}
              className={[
                'rounded-md px-2.5 py-1.5 text-sm font-medium',
                on ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50',
              ].join(' ')}
              title={
                opt.value === 'person'
                  ? 'One row per artist (writer/producer)'
                  : 'One row per song × artist × role'
              }
            >
              {opt.label}
            </button>
          )
        })}
      </div>
      {[
        { on: includeWriters, label: 'Writers', toggle: toggleWriters, other: includeProducers },
        { on: includeProducers, label: 'Producers', toggle: toggleProducers, other: includeWriters },
      ].map((opt) => (
        <button
          key={opt.label}
          type="button"
          onClick={opt.toggle}
          className={[
            'shrink-0 rounded-md border px-2 py-1 text-xs font-medium',
            opt.on
              ? 'border-gray-900 bg-gray-900 text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
          ].join(' ')}
          title={
            opt.on && !opt.other
              ? 'At least one credit type must stay on'
              : opt.on
                ? `Hide ${opt.label.toLowerCase()}`
                : `Show ${opt.label.toLowerCase()}`
          }
        >
          {opt.label}
        </button>
      ))}
      <label
        className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm"
        title="Destroy people and false credits are hidden by default"
      >
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
          checked={includeIgnored}
          onChange={(e) => setIncludeIgnored(e.target.checked)}
        />
        Show all
      </label>
      {availableCharts.length ? (
        <>
          {availableCharts.map((label) => {
            const on = selectedCharts.includes(label)
            const display = chartChipLabel(label)
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleChart(label)}
                className={[
                  'shrink-0 rounded-md border px-2 py-1 text-xs font-medium',
                  on
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                ].join(' ')}
                title={
                  on && selectedCharts.length <= 1
                    ? 'At least one chart must stay on'
                    : on
                      ? `Hide ${display}`
                      : `Show ${display}`
                }
              >
                {display}
              </button>
            )
          })}
          {chartsFilter ? (
            <button
              type="button"
              className="shrink-0 text-xs text-gray-500 underline hover:text-gray-800"
              onClick={() => setSelectedCharts([...availableCharts])}
            >
              All
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  )

  const defaultSort = useMemo(
    () =>
      isPersonGrain
        ? [
            { column: 'Best Rank', direction: 'asc' },
            { column: 'Name', direction: 'asc' },
          ]
        : [
            { column: 'Rank', direction: 'asc' },
            { column: 'Song', direction: 'asc' },
            { column: 'Name', direction: 'asc' },
          ],
    [isPersonGrain],
  )

  const hiddenColumns = useMemo(
    () =>
      isPersonGrain
        ? [
            'Pub Mapping ID',
            'Credit Details',
            'Credit History ID',
            'False Credit',
            // Person grain: song id is only meaningful for single-credit rows,
            // so it stays hidden; Artist ID is shown and links to Genius.
            'Genius Song ID',
            'Credit Type',
            'Genius URL',
            'New This Week',
          ]
        : [
            'Pub Mapping ID',
            'Credit History ID',
            'False Credit',
            'Genius URL',
            'New This Week',
          ],
    [isPersonGrain],
  )

  const headerLabels = useMemo(
    () => ({
      'Genius Artist ID': 'Artist ID',
      'Best Rank': 'Best',
      'Rank Change': 'Change',
    }),
    [],
  )

  /**
   * Name the week the rows actually come from. Charts are refreshed by a weekly
   * cron, so without a date a stale ingest looks identical to a current one.
   */
  const subtitle = useMemo(() => {
    const base = isPersonGrain
      ? "Artists credited on this week's radio + Hot 100 charts"
      : "Song / artist / credit rows on this week's radio + Hot 100 charts"
    const week = scope.scopeWeek || scope.ingestWeek
    if (!week) return base
    const label = formatChartWeek(week)
    const stale = weeksOld(scope.ingestWeek || scope.scopeWeek)
    const staleBit =
      stale != null && stale >= 2 ? ` · ⚠ ${stale} weeks old` : ''
    return `${base} · Chart week ${label}${staleBit}`
  }, [isPersonGrain, scope])

  const extraActions = (
    <button
      type="button"
      className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
      disabled={exportBusy}
      onClick={handleExport}
      title="Download the current view (filters included) as CSV"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {exportBusy ? 'Exporting…' : 'Export'}
    </button>
  )

  return (
    <div className="space-y-4">
      {exportError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Export failed: {exportError}
        </div>
      ) : null}
      <SummaryDashboard
      title="Charts"
      subtitle={subtitle}
      extraActions={extraActions}
      fetchData={fetchData}
      defaultSort={defaultSort}
      searchPlaceholder={
        isPersonGrain ? 'Search writer, producer…' : 'Search writer, producer, song…'
      }
      editableStatus={editableStatus}
      searchActions={searchActions}
      layout="mapping"
      legend={<PgpNewWeekLegend />}
      rowHighlightColumn="New This Week"
      truncateColumns={['Name', 'Chart']}
      hiddenColumns={hiddenColumns}
      headerLabels={headerLabels}
      placeholderColumns={isPersonGrain ? PLACEHOLDER_PGP_PERSON : PLACEHOLDER_PGP_CREDIT}
      tableKey={`pgp-charts-${grain}`}
      />
    </div>
  )
}
